/** @format */
import React from 'react'
import styled from 'styled-components'
import { Controlled } from 'react-codemirror2'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/material.css'
import 'codemirror/mode/python/python'
import 'codemirror/mode/shell/shell'
import { decodeExample } from '../utils'

interface ExampleCodeViewerProps {
  className?: string
  example: string | undefined | null
  lang?: 'python' | 'python3' | 'shell'
  readOnly?: boolean
  onChange?: (value: string) => void
}

const ExampleCodeViewer: React.FC<ExampleCodeViewerProps> = ({
  className,
  example,
  lang = 'python',
  readOnly = true,
  onChange
}) => {
  const decodedExample = decodeExample(example)
  const codeMode = lang === 'shell' ? 'shell' : 'python'

  // 如果可编辑模式，直接使用传入的 example（不需要解码，因为编辑时传入的是原始代码）
  const displayValue = readOnly ? decodedExample : example || ''

  if (!displayValue && readOnly) {
    return null
  }

  return (
    <div className={className}>
      <Controlled
        value={displayValue}
        options={{
          mode: codeMode,
          theme: 'material',
          lineNumbers: true,
          readOnly: readOnly,
          lineWrapping: true
        }}
        onBeforeChange={(editor, data, value) => {
          if (!readOnly && onChange) {
            onChange(value)
          }
        }}
      />
    </div>
  )
}

export default styled(ExampleCodeViewer)`
  border: none !important;
  border-radius: 0;
  overflow: hidden;
  margin-top: 8px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
  background: transparent !important;

  .CodeMirror {
    height: auto;
    min-height: 200px;
    font-size: 13px;
    line-height: 1.6;
    width: 100% !important;
    max-width: 100% !important;
    border: none !important;
  }

  .CodeMirror-scroll {
    width: 100% !important;
    max-width: 100% !important;
    overflow-x: auto !important;
    overflow-y: auto !important;
    /* 隐藏滚动条但保持滚动功能 */
    scrollbar-width: none !important; /* Firefox */
    -ms-overflow-style: none !important; /* IE 10+ */
  }

  /* 隐藏 WebKit 浏览器的滚动条 */
  .CodeMirror-scroll::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
  }

  .CodeMirror-scrollbar-filler {
    display: none !important;
  }

  .CodeMirror-gutter-filler {
    display: none !important;
  }

  .CodeMirror-sizer {
    min-width: 100% !important;
    box-sizing: border-box !important;
  }

  /* 确保 CodeMirror 内部元素也没有边框 */
  .CodeMirror-lines {
    border: none !important;
  }

  /* 隐藏行号区域的分割线 */
  .CodeMirror-gutters {
    border: none !important;
    border-right: none !important;
    border-left: none !important;
    border-top: none !important;
    border-bottom: none !important;
    background-color: transparent !important;
  }

  .CodeMirror-linenumber {
    border: none !important;
  }

  /* 隐藏 CodeMirror 的所有边框和分割线 */
  .CodeMirror,
  .CodeMirror-wrap,
  .CodeMirror-focused {
    border: none !important;
    outline: none !important;
  }

  /* 隐藏所有可能的内部边框和分割线 */
  .CodeMirror-gutter,
  .CodeMirror-gutter-wrapper,
  .CodeMirror-lines,
  .CodeMirror-line,
  .CodeMirror-line > span,
  .CodeMirror-line > span > span {
    border: none !important;
    border-top: none !important;
    border-bottom: none !important;
    border-left: none !important;
    border-right: none !important;
  }

  /* 隐藏所有伪元素产生的分割线 */
  .CodeMirror::before,
  .CodeMirror::after,
  .CodeMirror-gutters::before,
  .CodeMirror-gutters::after,
  .CodeMirror-lines::before,
  .CodeMirror-lines::after,
  .CodeMirror-scroll::before,
  .CodeMirror-scroll::after {
    display: none !important;
    content: none !important;
    border: none !important;
  }

  /* 确保 gutter 和内容区域之间没有分割线 */
  .CodeMirror-gutters + .CodeMirror-lines,
  .CodeMirror-lines + .CodeMirror-gutters {
    border: none !important;
  }

  /* 隐藏 CodeMirror 内部所有水平线 */
  .CodeMirror hr,
  .CodeMirror .cm-hr {
    display: none !important;
    border: none !important;
  }

  .CodeMirror-cursor {
    border-left: 1px solid #fff;
  }

  /* 针对所有 CodeMirror 子元素，强制移除水平边框 */
  .CodeMirror * {
    border-top: none !important;
    border-bottom: none !important;
  }

  /* 特别处理可能产生水平分割线的元素 */
  .CodeMirror-gutter-wrapper,
  .CodeMirror-gutter-elt,
  .CodeMirror-gutter {
    border-top: none !important;
    border-bottom: none !important;
    border-right: none !important;
  }

  /* 确保 CodeMirror 内部没有任何水平分割线 */
  .CodeMirror pre,
  .CodeMirror pre.CodeMirror-line {
    border-top: none !important;
    border-bottom: none !important;
  }
`
